import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import React from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { X, Plus, Trash2, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { motion } from "framer-motion";
import { Switch } from "@/components/ui/switch"
import { LABEL_COLOR_MAP, STATUS_COLORS } from '@/lib/colorsMap';
import { getIconComponent } from '@/lib/statusIconsMap';

const ProjectSettings = () => {
  const { organizationSlug, projectKey } = useParams();
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  const { toast } = useToast();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    defaultView: 'board',
    labels: [],
    statuses: []
  });

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axiosInstance.get(`/projects/${organizationSlug}/${projectKey}`);
        setProject(response.data);
        setFormData({
          name: response.data.name,
          key: response.data.key,
          description: response.data.description || '',
          defaultView: response.data.defaultView || 'board',
          labels: response.data.labels || [],
          statuses: response.data.statuses || []
        });
      } catch (error) {
        console.error('Error fetching project:', error);
        toast({
          description: "Failed to fetch project details",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectKey, organizationSlug]);

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      setHasChanges(true);
      return newData;
    });
  };

  const handleLabelChange = (index, field, value) => {
    setFormData(prev => {
      const newLabels = [...prev.labels];
      newLabels[index] = { ...newLabels[index], [field]: value };
      setHasChanges(true);
      return { ...prev, labels: newLabels };
    });
  };

  const handleAddLabel = () => {
    setFormData(prev => ({
      ...prev,
      labels: [...prev.labels, { name: '', color: 'bg-gray-500' }]
    }));
    setHasChanges(true);
  };

  const handleRemoveLabel = (index) => {
    setFormData(prev => ({
      ...prev,
      labels: prev.labels.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const handleStatusChange = (index, field, value) => {
    setFormData(prev => {
      const newStatuses = [...prev.statuses];
      newStatuses[index] = { ...newStatuses[index], [field]: value };
      setHasChanges(true);
      return { ...prev, statuses: newStatuses };
    });
  };

  const handleAddStatus = () => {
    // Generate a unique ID for the new status
    const newStatusId = `status_${Date.now()}`;

    setFormData(prev => ({
      ...prev,
      statuses: [
        ...prev.statuses,
        {
          id: newStatusId,
          name: 'New Status',
          color: 'gray',
          icon: 'circle'
        }
      ]
    }));
    setHasChanges(true);
  };

  const handleRemoveStatus = (index) => {
    // Don't allow removing default statuses
    const statusToRemove = formData.statuses[index];
    if (['backlog', 'todo', 'in_progress', 'in_review', 'done'].includes(statusToRemove.id)) {
      toast({
        description: "Default statuses cannot be removed",
        variant: "destructive"
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      statuses: prev.statuses.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const moveStatusUp = (index) => {
    if (index === 0) return; // Already at the top

    setFormData(prev => {
      const newStatuses = [...prev.statuses];
      // Swap with the previous item
      [newStatuses[index-1], newStatuses[index]] = [newStatuses[index], newStatuses[index-1]];
      setHasChanges(true);
      return { ...prev, statuses: newStatuses };
    });
  };

  const moveStatusDown = (index) => {
    setFormData(prev => {
      if (index === prev.statuses.length - 1) return prev; // Already at the bottom

      const newStatuses = [...prev.statuses];
      // Swap with the next item
      [newStatuses[index], newStatuses[index+1]] = [newStatuses[index+1], newStatuses[index]];
      setHasChanges(true);
      return { ...prev, statuses: newStatuses };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.patch(`/projects/${organizationSlug}/${projectKey}`, {
        name: formData.name,
        description: formData.description,
        defaultView: formData.defaultView,
        labels: formData.labels,
        statuses: formData.statuses
      });

      setProject(response.data);
      setHasChanges(false);
      toast({
        description: "Project settings updated successfully",
      });
    } catch (error) {
      console.error('Error updating project:', error);
      toast({
        description: "Failed to update project settings",
        variant: "destructive"
      });
    }
  };

  const handleDeleteProject = async () => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await axiosInstance.delete(`/projects/${organizationSlug}/${projectKey}`);
        toast({
          description: "Project deleted successfully",
        });
        navigate(`/${organizationSlug}`);
      } catch (error) {
        console.error('Error deleting project:', error);
        toast({
          description: "Failed to delete project",
          variant: "destructive"
        });
      }
    }
  };

  if (loading) {
    return <Layout>Loading...</Layout>;
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={`/${organizationSlug}`}>{project?.organizationName}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`/${organizationSlug}/plan/${projectKey}/board`}>{project?.name}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Settings</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Project Settings</h1>
              <p className="text-muted-foreground">
                Manage your project's settings and configuration
              </p>
            </div>
            {hasChanges && (
              <Button type="submit" onClick={handleSubmit}>
                Save Changes
              </Button>
            )}
          </div>
        </div>

        <Tabs defaultValue="general" className="space-y-4">
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="labels">Labels</TabsTrigger>
            <TabsTrigger value="statuses">Statuses</TabsTrigger>
            <TabsTrigger value="danger">Danger Zone</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Basic information about your project
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Project Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    placeholder="Add a project description..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="key">Project Key</Label>
                  <Input
                    id="key"
                    value={formData.key}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Project key cannot be changed after creation
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="defaultView">Default View</Label>
                  <Select
                    value={formData.defaultView}
                    onValueChange={(value) => handleFormChange('defaultView', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select default view" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="board">Board</SelectItem>
                      <SelectItem value="list">List</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="labels" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Labels</CardTitle>
                <CardDescription>
                  Manage project labels used for categorizing tasks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {formData.labels.map((label, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className={`w-2 h-8 rounded-sm ${label.color}`} />
                      <Input
                        value={label.name}
                        onChange={(e) => handleLabelChange(index, 'name', e.target.value)}
                        className="flex-1"
                        placeholder="Label name"
                      />
                      <Select
                        value={label.color}
                        onValueChange={(value) => handleLabelChange(index, 'color', value)}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue>
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded ${label.color}`} />
                              <span className="capitalize">
                                {label.color.replace('bg-', '').replace('-500', '')}
                              </span>
                            </div>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            'gray', 'red', 'yellow', 'green', 'blue',
                            'purple', 'pink', 'emerald', 'cyan'
                          ].map((color) => (
                            <SelectItem key={color} value={LABEL_COLOR_MAP[color]}>
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded ${LABEL_COLOR_MAP[color]}`} />
                                <span className="capitalize">{color}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveLabel(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    onClick={handleAddLabel}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Label
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="statuses" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Statuses</CardTitle>
                <CardDescription>
                  Manage project statuses used for tracking task progress
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {formData.statuses.map((status, index) => (
                    <div
                      key={`status-${index}`}
                      className="flex items-center gap-4 p-3 rounded-md border border-transparent hover:bg-muted/10"
                    >
                      <div className="flex flex-col">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveStatusUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveStatusDown(index)}
                          disabled={index === formData.statuses.length - 1}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>

                      <Select
                        value={status.icon}
                        onValueChange={(value) => handleStatusChange(index, 'icon', value)}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue>
                            <div className="flex items-center justify-center">
                              {React.createElement(getIconComponent(status.icon), {
                                className: `h-5 w-5 ${STATUS_COLORS[status.color]?.text || 'text-gray-500'}`
                              })}
                            </div>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {['circle-dashed', 'circle', 'circle-half', 'circle-eye', 'circle-check'].map((icon) => (
                            <SelectItem key={icon} value={icon}>
                              <div className="flex items-center gap-2">
                                {React.createElement(getIconComponent(icon), {
                                  className: "h-4 w-4"
                                })}
                                <span className="capitalize">{icon.replace('-', ' ')}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Input
                        value={status.name}
                        onChange={(e) => handleStatusChange(index, 'name', e.target.value)}
                        className="flex-1"
                        placeholder="Status name"
                      />

                      <Input
                        value={status.id}
                        disabled={['backlog', 'todo', 'in_progress', 'in_review', 'done'].includes(status.id)}
                        onChange={(e) => handleStatusChange(index, 'id', e.target.value)}
                        className="w-[120px] bg-muted disabled:opacity-50"
                        placeholder="Status ID"
                      />

                      <Select
                        value={status.color}
                        onValueChange={(value) => handleStatusChange(index, 'color', value)}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue>
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded ${STATUS_COLORS[status.color]?.bg || 'bg-gray-500'}`} />
                              <span className="capitalize">
                                {status.color}
                              </span>
                            </div>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(STATUS_COLORS).map((color) => (
                            <SelectItem key={color} value={color}>
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded ${STATUS_COLORS[color]?.bg || 'bg-gray-500'}`} />
                                <span className="capitalize">{color}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveStatus(index)}
                        disabled={['backlog', 'todo', 'in_progress', 'in_review', 'done'].includes(status.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <Button
                  variant="outline"
                  onClick={handleAddStatus}
                  className="w-full mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Status
                </Button>

                <div className="mt-4 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Note: Default statuses (Backlog, To Do, In Progress, In Review, Done) cannot be removed.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Use the up and down arrows to reorder statuses. The order affects how tasks progress through your workflow.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="danger" className="space-y-4">
            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="text-destructive">Danger Zone</CardTitle>
                <CardDescription>
                  Irreversible and destructive actions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Delete Project</h4>
                      <p className="text-sm text-muted-foreground">
                        Permanently delete this project and all of its data
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteProject}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Project
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default ProjectSettings;